import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, LogOut } from "lucide-react";
import { fetchCustodePoints, firstNameFromUser } from "../../utils/passportPoints";

/**
 * Widget header: avatar + nome breve (solo ≥1440px) con menu a tendina
 * contenente il saluto completo, punti e "Esci".
 */
export default function CustodeProfileWidget({ user, compact = false, onLogout }) {
  const [points, setPoints] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const nome = firstNameFromUser(user);
  const shortName = nome && nome.length > 12 ? `${nome.slice(0, 12)}…` : nome;

  useEffect(() => {
    if (!user) {
      setPoints(null);
      return undefined;
    }
    let cancelled = false;
    fetchCustodePoints().then((n) => {
      if (!cancelled) setPoints(n);
    });
    const bump = () => {
      fetchCustodePoints().then((n) => {
        if (!cancelled) setPoints(n);
      });
    };
    window.addEventListener("passaporto:update", bump);
    window.addEventListener("6amico_timbro_aggiunto", bump);
    return () => {
      cancelled = true;
      window.removeEventListener("passaporto:update", bump);
      window.removeEventListener("6amico_timbro_aggiunto", bump);
    };
  }, [user]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onDoc(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  if (!user) return null;

  const ptsLabel = points == null ? "…" : `${points} pt`;
  const avatar = user.picture ? (
    <img
      src={user.picture}
      alt=""
      className="w-7 h-7 rounded-full object-cover shrink-0"
      width={28}
      height={28}
    />
  ) : (
    <span
      className="w-7 h-7 rounded-full bg-[#162d4a] text-white text-xs font-bold inline-flex items-center justify-center shrink-0"
      aria-hidden
    >
      {(nome || "C").charAt(0).toUpperCase()}
    </span>
  );

  if (compact) {
    return (
      <Link
        to="/profilo"
        className="min-h-[48px] flex items-center gap-2 px-3 rounded-lg text-base font-sans font-semibold text-stone-900 hover:bg-[#f7efe6]"
        data-testid="custode-profile-widget"
        title="Il tuo profilo Custode"
      >
        {avatar}
        <span className="truncate">Ciao {nome}!</span>
      </Link>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="min-h-[44px] inline-flex items-center gap-1.5 px-2.5 rounded-full border border-[#ecd9c2] bg-[#f7efe6] text-sm font-sans font-semibold text-stone-900 hover:border-[#162d4a] transition-colors"
        data-testid="custode-profile-widget"
        title="Il tuo profilo Custode"
      >
        {avatar}
        <span className="hidden min-[1440px]:inline max-w-[110px] truncate">{shortName}</span>
        <ChevronDown size={13} className={`transition-transform ${menuOpen ? "rotate-180" : ""}`} aria-hidden />
      </button>
      {menuOpen && (
        <div
          role="menu"
          aria-label="Menu profilo"
          className="absolute right-0 mt-1.5 w-56 rounded-xl bg-white border border-stone-200 shadow-xl py-1.5 z-50"
        >
          <div className="px-4 py-2.5 border-b border-stone-100">
            <p className="text-sm font-sans font-semibold text-stone-900 truncate">Ciao {nome}!</p>
            <p className="text-xs font-mono text-stone-500 mt-0.5">🏅 {ptsLabel} Passaporto</p>
          </div>
          <Link
            to="/profilo"
            role="menuitem"
            onClick={() => setMenuOpen(false)}
            className="w-full flex items-center gap-2.5 px-4 min-h-[44px] text-sm text-stone-800 hover:bg-[#f7efe6]"
          >
            Il tuo profilo
          </Link>
          <Link
            to="/passaporto"
            role="menuitem"
            onClick={() => setMenuOpen(false)}
            className="w-full flex items-center gap-2.5 px-4 min-h-[44px] text-sm text-stone-800 hover:bg-[#f7efe6]"
          >
            Passaporto del Custode
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              if (onLogout) onLogout();
            }}
            className="w-full flex items-center gap-2.5 px-4 min-h-[44px] text-sm text-stone-800 hover:bg-[#f7efe6]"
          >
            <LogOut size={15} className="text-stone-500" aria-hidden />
            Esci
          </button>
        </div>
      )}
    </div>
  );
}
